import { useRef, useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, Upload, X, FileText, FileSpreadsheet, Image as ImageIcon,
  File as FileIcon, Presentation, Sparkles, RefreshCw, Save, FolderOpen, Trash2,
  Download, Database, Share2, GitCompare, Clock, Copy, Check, ShieldCheck, Cloud,
} from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ConsultReport, asArr, finite, fmt } from "./ConsultReport";
import "./HBMConsult.css";

const STAGES = [
  ["Secure evidence", "Hash, file policy and instruction isolation"],
  ["Inspect corpus", "Read tables, transcripts, images and clues"],
  ["Resolve subject", "Identify entity, sector and reconcile facts"],
  ["Search globally", "Inspect primary, sector and news sources"],
  ["Challenge evidence", "Contradictions, stale claims, alternatives"],
  ["Run methods", "Financial, strategic and causal models"],
  ["Synthesize", "Triangulate conclusions, confidence and gaps"],
  ["Compile", "Assemble decision dashboards and memo"],
];

function fileIcon(name = "") {
  const l = name.toLowerCase();
  if (/\.(xlsx?|csv)$/.test(l)) return <FileSpreadsheet size={16} />;
  if (/\.(png|jpe?g|webp|tiff?|gif)$/.test(l)) return <ImageIcon size={16} />;
  if (/\.pptx?$/.test(l)) return <Presentation size={16} />;
  if (/\.(pdf|docx?|txt|md)$/.test(l)) return <FileText size={16} />;
  return <FileIcon size={16} />;
}

const EXPORTS = [
  ["pdf", "PDF"], ["docx", "Word"], ["xlsx", "Excel"], ["pptx", "PowerPoint"], ["json", "JSON"], ["csv", "CSV"],
];

const MODES = ["Quick", "Standard", "Deep", "Extended"];

export default function HBMConsult() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [question, setQuestion] = useState("What should management do next, based on this evidence?");
  const [objective, setObjective] = useState("Produce a decision-ready recommendation with alternatives, risks, evidence gaps and execution gates.");
  const [constraints, setConstraints] = useState("");
  const [horizon, setHorizon] = useState("3-12 months");
  const [mode, setMode] = useState("Deep");
  const [stage, setStage] = useState("ready");
  const [step, setStep] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [demoBuilding, setDemoBuilding] = useState(false);
  const [demoAvailable, setDemoAvailable] = useState(false);
  const [exporting, setExporting] = useState("");
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showProjects, setShowProjects] = useState(false);
  const [showDpo, setShowDpo] = useState(false);
  const [dpoStats, setDpoStats] = useState(null);
  const [compareSel, setCompareSel] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [copied, setCopied] = useState("");
  const [graphStatus, setGraphStatus] = useState(null);
  const [graphUploading, setGraphUploading] = useState("");
  const inputRef = useRef(null);
  const busy = stage === "running";

  useEffect(() => {
    if (user?.role !== "admin") return;
    fetch(`${API}/admin/consult/demo`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setDemoAvailable(!!d.available)).catch(() => {});
    fetch(`${API}/admin/consult/graph/status`, { credentials: "include" })
      .then((r) => r.json()).then(setGraphStatus).catch(() => setGraphStatus({ configured: false, missing: ["status_unavailable"] }));
    refreshProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[var(--text-muted)]">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: "/admin/hbm-consult" }} replace />;
  if (user?.role !== "admin") return <Navigate to="/" replace />;

  function addFiles(list) { setFiles([...files, ...Array.from(list || [])].slice(0, 12)); }

  async function pollJob(jobId) {
    for (let i = 0; i < 160; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const r = await fetch(`${API}/admin/consult/job/${jobId}`, { credentials: "include" });
      if (!r.ok) continue;
      const d = await r.json();
      if (d.status === "complete") return d.analysis;
      if (d.status === "error") throw new Error(d.error || "Analysis failed");
    }
    throw new Error("The analysis is taking longer than expected. Please try again with a smaller pack.");
  }

  function startTimers() {
    return [3000, 9000, 18000, 30000, 48000, 72000, 100000, 130000].map((ms, i) => setTimeout(() => setStep(i + 1), ms));
  }

  async function run() {
    if (!files.length) { inputRef.current?.click(); return; }
    setError(""); setAnalysis(null); setStage("running"); setStep(0);
    const timers = startTimers();
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      form.append("question", question);
      form.append("objective", objective);
      form.append("constraints", constraints);
      form.append("horizon", horizon);
      form.append("mode", mode);
      const r = await fetch(`${API}/admin/consult/analyze`, { method: "POST", credentials: "include", body: form });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      const result = await pollJob(data.job_id);
      setStep(8); setAnalysis(result); setStage("complete");
      toast.success("Analysis complete.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setError(e?.message || String(e)); setStage("error"); toast.error(e?.message || "Analysis failed"); }
    finally { timers.forEach(clearTimeout); }
  }

  async function loadDemo() {
    try {
      const r = await fetch(`${API}/admin/consult/demo`, { credentials: "include" });
      const d = await r.json();
      if (!d.available) { toast.error("No sample project built yet. Use 'Build sample'."); return; }
      setAnalysis(d.analysis); setStage("complete");
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Sample Microsoft project loaded.");
    } catch { toast.error("Could not load sample."); }
  }

  async function buildDemo() {
    setDemoBuilding(true); setError(""); setStage("running"); setStep(0);
    const timers = startTimers();
    try {
      const r = await fetch(`${API}/admin/consult/demo/rebuild`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      const result = await pollJob(d.job_id);
      setDemoAvailable(true); setStep(8); setAnalysis(result); setStage("complete");
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Sample Microsoft project built.");
    } catch (e) { setError(e?.message || String(e)); setStage("error"); toast.error("Build failed — see error."); }
    finally { setDemoBuilding(false); timers.forEach(clearTimeout); }
  }

  async function exportAs(format) {
    if (!analysis) return;
    setExporting(format);
    try {
      const r = await fetch(`${API}/admin/consult/export`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ format, analysis }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const ext = format === "pdf_client" ? "pdf" : format;
      const url = URL.createObjectURL(blob), aEl = document.createElement("a");
      aEl.href = url; aEl.download = `HBM-Consult${format === "pdf_client" ? "-client" : ""}.${ext}`; aEl.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Export failed."); }
    finally { setExporting(""); }
  }

  async function saveToMicrosoft(format) {
    if (!analysis) return;
    setGraphUploading(format);
    try {
      const r = await fetch(`${API}/admin/consult/graph/upload`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ format, analysis }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail?.message || d?.detail || `HTTP ${r.status}`);
      toast.success("Saved to Microsoft 365.");
      if (d.web_url) window.open(d.web_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(String(e?.message || e || "Microsoft Graph is not configured yet."));
    } finally { setGraphUploading(""); }
  }

  /* ---- projects ---- */
  async function refreshProjects() {
    try {
      const r = await fetch(`${API}/admin/consult/projects`, { credentials: "include" });
      const d = await r.json();
      setProjects(d.projects || []);
    } catch { /* ignore */ }
  }

  async function saveProject() {
    if (!analysis) return;
    const name = window.prompt("Name this consulting project", `${analysis.company || "Analysis"} — ${new Date().toISOString().slice(0, 10)}`);
    if (!name) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/consult/projects`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, analysis }),
      });
      if (!r.ok) throw new Error();
      await refreshProjects();
      toast.success("Project saved.");
    } catch { toast.error("Could not save project."); }
    finally { setSaving(false); }
  }

  async function openProject(id) {
    try {
      const r = await fetch(`${API}/admin/consult/projects/${id}`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error();
      setAnalysis(d.analysis); setStage("complete"); setShowProjects(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Could not open project."); }
  }

  async function deleteProject(id, e) {
    e?.stopPropagation();
    if (!window.confirm("Delete this saved project?")) return;
    try { await fetch(`${API}/admin/consult/projects/${id}`, { method: "DELETE", credentials: "include" }); await refreshProjects(); }
    catch { toast.error("Delete failed."); }
  }

  async function toggleShare(p, e) {
    e?.stopPropagation();
    try {
      const r = await fetch(`${API}/admin/consult/projects/${p.id}/share`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enable: !p.shared }),
      });
      const d = await r.json();
      await refreshProjects();
      if (d.shared) {
        const link = `${window.location.origin}/consult/share/${d.share_id}`;
        navigator.clipboard?.writeText(link).catch(() => {});
        setCopied(p.id); setTimeout(() => setCopied(""), 2000);
        toast.success("Public link created & copied.");
      } else toast.success("Sharing disabled.");
    } catch { toast.error("Could not update sharing."); }
  }

  async function refreshProject(p, e) {
    e?.stopPropagation();
    toast.message("Refreshing project — re-running research…");
    try {
      const r = await fetch(`${API}/admin/consult/projects/${p.id}/refresh`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "refresh failed");
      const fresh = await pollJob(d.job_id);
      await refreshProjects();
      setAnalysis(fresh); setStage("complete"); setShowProjects(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Project refreshed with the latest research.");
    } catch (e2) { toast.error(e2?.message || "Refresh unavailable for this project."); }
  }

  async function toggleAuto(p, e) {
    e?.stopPropagation();
    try {
      await fetch(`${API}/admin/consult/projects/${p.id}/auto-refresh`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enable: !p.auto_refresh, interval_days: 7 }),
      });
      await refreshProjects();
      toast.success(!p.auto_refresh ? "Weekly auto-refresh on." : "Auto-refresh off.");
    } catch { toast.error("Could not update auto-refresh."); }
  }

  function toggleCompareSel(id, e) {
    e?.stopPropagation();
    setCompareSel((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 2 ? [s[1], id] : [...s, id]));
  }

  async function runCompare() {
    if (compareSel.length !== 2) { toast.error("Select exactly two projects to compare."); return; }
    try {
      const [a, b] = await Promise.all(compareSel.map((id) =>
        fetch(`${API}/admin/consult/projects/${id}`, { credentials: "include" }).then((r) => r.json())));
      setCompareData([a, b]); setShowProjects(false);
    } catch { toast.error("Could not load projects to compare."); }
  }

  /* ---- dpo ---- */
  async function openDpo() {
    setShowDpo(true);
    if (dpoStats) return;
    try { const r = await fetch(`${API}/admin/consult/dpo-stats`, { credentials: "include" }); setDpoStats(await r.json()); }
    catch { setDpoStats({ error: true }); }
  }
  function downloadDpo(style, limit) {
    const link = document.createElement("a");
    link.href = `${API}/admin/consult/dpo-export?style=${style}&limit=${limit}`;
    link.target = "_blank"; link.rel = "noopener"; link.download = `hbm-dpo-${style}-${limit}.jsonl`;
    document.body.appendChild(link); link.click(); setTimeout(() => link.remove(), 1500);
    toast.success("Preparing dataset download…");
  }

  const a = analysis;
  return (
    <div className="hbmc" style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 22px 80px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Link to="/admin" className="hbmc-ghost" data-testid="hbm-consult-back" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><ArrowLeft size={13} /> Admin</Link>
            <span className="kicker">HBM Consult · Decision Intelligence</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="hbmc-ghost" onClick={() => { setShowProjects(true); refreshProjects(); }} data-testid="open-projects-btn"><FolderOpen size={12} style={{ display: "inline", marginRight: 4 }} />Projects{projects.length ? ` (${projects.length})` : ""}</button>
            <button className="hbmc-ghost" onClick={openDpo} data-testid="open-dpo-btn"><Database size={12} style={{ display: "inline", marginRight: 4 }} />Fine-tune data</button>
            {demoAvailable && <button className="hbmc-ghost" onClick={loadDemo} data-testid="load-demo-btn"><Sparkles size={12} style={{ display: "inline", marginRight: 4 }} />Sample project</button>}
            <button className="hbmc-ghost" onClick={buildDemo} disabled={demoBuilding} data-testid="build-demo-btn">
              {demoBuilding ? <Loader2 size={12} className="animate-spin" style={{ display: "inline", marginRight: 4 }} /> : <RefreshCw size={12} style={{ display: "inline", marginRight: 4 }} />}
              {demoBuilding ? "Building sample…" : "Build sample (Microsoft)"}
            </button>
          </div>
        </div>

        <header style={{ margin: "26px 0 20px" }}>
          <div className="kicker">Admin-only · PRD rebuilt decision intelligence</div>
          <h1 style={{ fontFamily: "Instrument Serif,serif", fontSize: 44, lineHeight: 1.05, margin: "6px 0 0", letterSpacing: "-.02em" }}>
            Understand first. Analyze deeply. Export the decision.
          </h1>
          <div className="hbmc-grid g2" style={{ marginTop: 18 }}>
            <div className="hbmc-card" data-testid="consult-principle-evidence"><ShieldCheck size={16} /><b style={{ display: "block", marginTop: 6 }}>Evidence before opinion</b><p className="dim" style={{ fontSize: 12 }}>Facts, assumptions, inferences and recommendations remain separated.</p></div>
            <div className="hbmc-card" data-testid="consult-principle-process"><Sparkles size={16} /><b style={{ display: "block", marginTop: 6 }}>23-stage analysis pipeline</b><p className="dim" style={{ fontSize: 12 }}>Question extraction, domain routing, alternatives, counterarguments and audit.</p></div>
          </div>
        </header>

        {stage !== "complete" && (
          <div className="hbmc-upload" data-testid="consult-upload">
            <div className="kicker">New investigation</div>
            <p className="muted" style={{ margin: "8px 0 16px", fontSize: 14, maxWidth: "72ch" }}>
              Create a consultation with problem, objective, constraints, decision horizon and evidence. Files are stored in Object Storage, then converted into an auditable decision workspace before any report is written.
            </p>
            <div className="hbmc-modebar" data-testid="consult-analysis-mode-selector">
              {MODES.map((m) => <button key={m} type="button" className={mode === m ? "on" : ""} onClick={() => setMode(m)} data-testid={`consult-mode-${m.toLowerCase()}`}>{m}</button>)}
            </div>
            <input ref={inputRef} hidden multiple type="file"
              accept=".zip,.pdf,.docx,.pptx,.xlsx,.xls,.csv,.json,.xml,.txt,.md,.html,.htm,.png,.jpg,.jpeg,.webp,.tif,.tiff"
              onChange={(e) => { addFiles(e.target.files); e.currentTarget.value = ""; }} data-testid="consult-file-input" />
            <div className={`hbmc-drop ${dragOver ? "over" : ""}`} onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}>
              <div className="fic"><Upload size={18} /></div>
              <div>
                <b style={{ fontSize: 14 }}>{files.length ? `${files.length} evidence file${files.length > 1 ? "s" : ""} selected` : "Add primary, supporting or ZIP files"}</b>
                <div className="dim" style={{ fontSize: 12 }}>{files.length ? `${(files.reduce((n, f) => n + f.size, 0) / 1024 / 1024).toFixed(1)} MB · first file is primary` : "PDF · DOCX · PPTX · XLSX · CSV · images · up to 250 MB"}</div>
              </div>
            </div>
            {files.map((f, i) => (
              <div className="fileChip" key={i}>
                <span className="tag">{i === 0 ? "PRIMARY" : "SUPPORT"}</span>
                {fileIcon(f.name)}<span>{f.name}</span>
                <span className="dim" style={{ fontSize: 11 }}>{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => setFiles(files.filter((_, x) => x !== i))} aria-label="remove"><X size={14} /></button>
              </div>
            ))}
            <label style={{ display: "block", margin: "16px 0 6px" }}><div className="kicker">Decision question</div></label>
            <textarea className="hbmc-textarea" rows={2} value={question} onChange={(e) => setQuestion(e.target.value)} disabled={busy} data-testid="consult-question" />
            <div className="hbmc-grid g3" style={{ marginTop: 12 }}>
              <div>
                <label className="kicker" htmlFor="consult-objective">Objective</label>
                <textarea id="consult-objective" className="hbmc-textarea" rows={3} value={objective} onChange={(e) => setObjective(e.target.value)} disabled={busy} data-testid="consult-objective" />
              </div>
              <div>
                <label className="kicker" htmlFor="consult-constraints">Constraints</label>
                <textarea id="consult-constraints" className="hbmc-textarea" rows={3} value={constraints} onChange={(e) => setConstraints(e.target.value)} disabled={busy} placeholder="Capital, timing, regulation, capability, risk tolerance…" data-testid="consult-constraints" />
              </div>
              <div>
                <label className="kicker" htmlFor="consult-horizon">Decision horizon</label>
                <select id="consult-horizon" className="hbmc-select" value={horizon} onChange={(e) => setHorizon(e.target.value)} disabled={busy} data-testid="consult-horizon-select">
                  <option>Immediate / 0-30 days</option><option>1-3 months</option><option>3-12 months</option><option>12+ months</option><option>Strategic / multi-year</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button className="hbmc-run" onClick={run} disabled={busy || !files.length} data-testid="consult-run-btn">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Analysing…</> : <>Run {mode} consultation →</>}
              </button>
            </div>
            {(busy || error) && (
              <div style={{ marginTop: 16 }}>
                <div className="hbmc-steps">
                  {STAGES.map((s, i) => (
                    <div key={s[0]} className={step === i ? "active" : step > i ? "complete" : ""}>
                      <i>{String(i + 1).padStart(2, "0")}</i><b>{s[0]}</b><small>{s[1]}</small>
                    </div>
                  ))}
                </div>
                {error && <p style={{ color: "var(--danger)", fontSize: 13, marginTop: 10 }} data-testid="consult-error">{error}</p>}
              </div>
            )}
          </div>
        )}

        {a && stage === "complete" && (
          <div data-testid="consult-results">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginTop: 20 }}>
              <div>
                <div className="kicker">{a.reportState || "Analysis complete"} · {a.document?.files?.length || 1} source files · {a.consultation?.mode || mode}</div>
                <h2 className="sec" style={{ maxWidth: "62ch" }}>{a.displayQuestion || a.consultation?.problem || a.question}</h2>
                <small className="dim">{a.document?.characters?.toLocaleString()} characters · checksum {String(a.document?.sha256 || "").slice(0, 12)}…</small>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="hbmc-ghost" onClick={saveProject} disabled={saving} data-testid="save-project-btn">
                  {saving ? <Loader2 size={12} className="animate-spin" style={{ display: "inline" }} /> : <Save size={12} style={{ display: "inline", marginRight: 4 }} />}Save
                </button>
                <button className="hbmc-ghost" onClick={() => { setStage("ready"); setAnalysis(null); setFiles([]); }} data-testid="new-investigation-btn">＋ New</button>
                {EXPORTS.map(([f, label]) => (
                  <button className="hbmc-ghost" key={f} onClick={() => exportAs(f)} disabled={exporting === f} data-testid={`export-${f}`}>
                    {exporting === f ? <Loader2 size={12} className="animate-spin" style={{ display: "inline" }} /> : label} ↓
                  </button>
                ))}
                <button className="hbmc-run" style={{ padding: "10px 14px" }} onClick={() => exportAs("pdf_client")} disabled={exporting === "pdf_client"} data-testid="export-client-pdf">
                  {exporting === "pdf_client" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Client PDF
                </button>
                {graphStatus?.configured && (
                  <button className="hbmc-ghost" onClick={() => saveToMicrosoft("docx")} disabled={!!graphUploading} data-testid="save-microsoft-docx-btn" title="Save Word report to Microsoft 365">
                    {graphUploading === "docx" ? <Loader2 size={12} className="animate-spin" style={{ display: "inline" }} /> : <Cloud size={12} style={{ display: "inline", marginRight: 4 }} />}Microsoft 365
                  </button>
                )}
              </div>
            </div>
            {a.processTrace?.length > 0 && (
              <div className="hbmc-card" style={{ marginTop: 14 }} data-testid="consult-process-trace">
                <div className="lbl"><span>Consult process trace</span><b>{a.processTrace.length} stages</b></div>
                <div className="processRail">
                  {a.processTrace.map((p, i) => <div key={i}><i>{String(i + 1).padStart(2, "0")}</i><b>{p.stage}</b><small>{p.evidence}</small></div>)}
                </div>
              </div>
            )}
            <ConsultReport a={a} />
          </div>
        )}
      </div>

      {/* projects drawer */}
      {showProjects && (
        <div className="hbmc-overlay" onClick={() => setShowProjects(false)} data-testid="projects-drawer">
          <aside className="hbmc-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawerHead">
              <div><div className="kicker">Saved consulting projects</div><h3 className="sub">{projects.length} project{projects.length === 1 ? "" : "s"}</h3></div>
              <button className="hbmc-ghost" onClick={() => setShowProjects(false)}><X size={14} /></button>
            </div>
            {compareSel.length > 0 && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                <span className="dim" style={{ fontSize: 12 }}>{compareSel.length}/2 selected</span>
                <button className="hbmc-run" style={{ padding: "8px 14px" }} onClick={runCompare} disabled={compareSel.length !== 2} data-testid="run-compare-btn"><GitCompare size={12} /> Compare</button>
                <button className="hbmc-ghost" onClick={() => setCompareSel([])}>Clear</button>
              </div>
            )}
            {projects.length === 0 && <p className="muted" style={{ fontSize: 13, padding: "20px 0" }}>No saved projects yet. Run or load an analysis, then click “Save”.</p>}
            {projects.map((p) => (
              <div className="projRow" key={p.id} data-testid="project-row">
                <input type="checkbox" checked={compareSel.includes(p.id)} onChange={(e) => toggleCompareSel(p.id, e)} onClick={(e) => e.stopPropagation()} title="Select to compare" data-testid="compare-checkbox" style={{ cursor: "pointer" }} />
                <div style={{ flex: 1, minWidth: 0 }} onClick={() => openProject(p.id)}>
                  <b style={{ fontSize: 14, display: "block" }}>{p.name}</b>
                  <small className="dim">{[p.company, p.ticker, finite(p.confidence) ? `${p.confidence}% conf` : null, (p.created_at || "").slice(0, 10)].filter(Boolean).join(" · ")}</small>
                  <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                    {p.shared && <span className="pill" style={{ margin: 0, color: "var(--success)" }}>SHARED</span>}
                    {p.auto_refresh && <span className="pill" style={{ margin: 0 }}>WEEKLY REFRESH</span>}
                    {p.last_refreshed && <span className="pill" style={{ margin: 0 }}>↻ {String(p.last_refreshed).slice(0, 10)}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <button className="iconBtn" title={p.shared ? "Copy / disable share" : "Create public link"} onClick={(e) => toggleShare(p, e)} data-testid="share-project-btn">{copied === p.id ? <Check size={13} /> : <Share2 size={13} />}</button>
                  <button className="iconBtn" title="Refresh now" onClick={(e) => refreshProject(p, e)} data-testid="refresh-project-btn"><RefreshCw size={13} /></button>
                  <button className="iconBtn" title="Toggle weekly auto-refresh" onClick={(e) => toggleAuto(p, e)} data-testid="auto-refresh-btn" style={p.auto_refresh ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}}><Clock size={13} /></button>
                  <button className="iconBtn" title="Delete" onClick={(e) => deleteProject(p.id, e)} data-testid="delete-project-btn"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </aside>
        </div>
      )}

      {/* compare modal */}
      {compareData && (
        <div className="hbmc-overlay" onClick={() => setCompareData(null)} data-testid="compare-modal">
          <div className="hbmc-modal" style={{ width: "min(900px,94vw)" }} onClick={(e) => e.stopPropagation()}>
            <div className="drawerHead"><div><div className="kicker">Side-by-side comparison</div><h3 className="sub">Two saved projects</h3></div><button className="hbmc-ghost" onClick={() => setCompareData(null)}><X size={14} /></button></div>
            <div className="hbmc-grid g2">
              {compareData.map((proj, idx) => {
                const an = proj.analysis || {};
                const ai = an.deepAnalysis || {};
                const conf = an.confidence || {};
                const scores = asArr(ai.decisionScorecard).filter((s) => finite(s.score) && Number(s.weight) > 0);
                const totalW = scores.reduce((n, s) => n + Number(s.weight), 0);
                const weighted = scores.length && totalW ? Math.round(scores.reduce((n, s) => n + Number(s.score) * Number(s.weight), 0) / totalW) : "—";
                const dec = an.memo?.decision;
                return (
                  <div className="hbmc-card" key={idx}>
                    <div className="kicker">{proj.name}</div>
                    <h3 className="sub" style={{ fontSize: 17, margin: "4px 0 10px" }}>{an.company || "—"}</h3>
                    <div className="hbmc-metrics" style={{ margin: "0 0 12px", gridTemplateColumns: "1fr 1fr" }}>
                      <div><span>Confidence</span><b style={{ fontSize: 22 }}>{finite(conf.overall) ? `${conf.overall}%` : "—"}</b><em>{conf.band}</em></div>
                      <div><span>Weighted score</span><b style={{ fontSize: 22 }}>{weighted}{weighted !== "—" ? "/100" : ""}</b></div>
                      <div><span>Reconciled facts</span><b style={{ fontSize: 20 }}>{conf.reconciledFacts ?? "—"}</b></div>
                      <div><span>Sources</span><b style={{ fontSize: 20 }}>{conf.sourcesInspected ?? "—"}</b></div>
                    </div>
                    <span className="kicker">Recommendation</span>
                    <p style={{ fontSize: 13 }}>{dec?.title || "—"}</p>
                    <p className="dim" style={{ fontSize: 12 }}>{(dec?.body || "").slice(0, 220)}{(dec?.body || "").length > 220 ? "…" : ""}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* dpo modal */}
      {showDpo && (
        <div className="hbmc-overlay" onClick={() => setShowDpo(false)} data-testid="dpo-modal">
          <div className="hbmc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drawerHead"><div><div className="kicker">Fine-tuning dataset</div><h3 className="sub">Direct Preference Optimization export</h3></div><button className="hbmc-ghost" onClick={() => setShowDpo(false)}><X size={14} /></button></div>
            <p className="muted" style={{ fontSize: 13 }}>Preference pairs from the gold behavioural corpus — <strong>prompt</strong> = scenario, <strong>preferred</strong> = mechanism-first answer, <strong>rejected</strong> = the surface reading. Ready for preference fine-tuning.</p>
            {!dpoStats ? <p className="dim" style={{ fontSize: 12 }}><Loader2 size={12} className="animate-spin" style={{ display: "inline" }} /> Loading corpus stats…</p> : dpoStats.error ? <p style={{ color: "var(--danger)", fontSize: 12 }}>Could not load corpus stats.</p> : (
              <>
                <div className="hbmc-metrics" style={{ margin: "14px 0" }}>
                  <div><span>Total cases</span><b>{Number(dpoStats.total_cases).toLocaleString()}</b></div>
                  <div><span>Domains</span><b>{(dpoStats.by_domain || []).length}</b></div>
                  <div><span>Top domain</span><b style={{ fontSize: 15 }}>{dpoStats.by_domain?.[0]?.domain || "—"}</b><em>{dpoStats.by_domain?.[0]?.count?.toLocaleString()}</em></div>
                  <div><span>Pair fields</span><b style={{ fontSize: 15 }}>prompt · chosen · rejected</b></div>
                </div>
                <div className="kicker" style={{ marginBottom: 8 }}>Download JSONL</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="hbmc-run" onClick={() => downloadDpo("trl", 100000)} data-testid="dpo-download-trl"><Download size={13} /> TRL format (full)</button>
                  <button className="hbmc-ghost" onClick={() => downloadDpo("together", 100000)} data-testid="dpo-download-together"><Download size={12} style={{ display: "inline", marginRight: 4 }} />Together format (full)</button>
                  <button className="hbmc-ghost" onClick={() => downloadDpo("trl", 500)}>Sample (500)</button>
                </div>
                <p className="dim" style={{ fontSize: 11, marginTop: 12 }}>CLI equivalent: <code>python dpo_export.py --out exports/dpo.jsonl --style trl</code></p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
